import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, DatePicker, Select, Table, Tooltip, message } from "antd";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchSprTov } from "@components/health/clients/health-clients-api";
import { fetchSprTov as fetchSprTovItem } from "../sprs/tov/spr-tov-api";
import { fetchWarehouseLookups, fetchWarehouseProductCard } from "./warehouse-api";
import {
  formatDate,
  formatMoveVid,
  formatNumber,
  normalizeLookups,
  pick,
  toNumber
} from "./warehouse-utils";
import "./warehouse.css";

const normalizeProductOptions = (data) => {
  const rows = Array.isArray(data) ? data : pick(data, "items", "Items") || [];
  return rows
    .map((row) => ({
      value: toNumber(pick(row, "idTov", "IdTov", "id", "Id")),
      label: pick(row, "name", "Name", "productName", "ProductName") ?? ""
    }))
    .filter((row) => row.value != null);
};

const normalizeCardLine = (row = {}, index = 0) => ({
  ...row,
  key: String(pick(row, "idReg", "IdReg") ?? `line-${index}`),
  idReg: toNumber(pick(row, "idReg", "IdReg")),
  fdate: pick(row, "fdate", "Fdate") ?? null,
  idSkl: toNumber(pick(row, "idSkl", "IdSkl")),
  sklName: pick(row, "sklName", "SklName") ?? "",
  vid: toNumber(pick(row, "vid", "Vid"), 0),
  vidOsn: toNumber(pick(row, "vidOsn", "VidOsn"), 0),
  idOsn: toNumber(pick(row, "idOsn", "IdOsn")),
  idOsnStr: toNumber(pick(row, "idOsnStr", "IdOsnStr")),
  kol: toNumber(pick(row, "kol", "Kol"), 0),
  rest: toNumber(pick(row, "rest", "Rest"), 0)
});

const normalizeCard = (data = {}) => {
  const rawItems = pick(data, "items", "Items");
  return {
    idTov: toNumber(pick(data, "idTov", "IdTov")),
    tovName: pick(data, "tovName", "TovName") ?? "",
    funit: pick(data, "funit", "Funit") ?? "",
    kolBeg: toNumber(pick(data, "kolBeg", "KolBeg"), 0),
    kolPut: toNumber(pick(data, "kolPut", "KolPut"), 0),
    kolExp: toNumber(pick(data, "kolExp", "KolExp"), 0),
    kolEnd: toNumber(pick(data, "kolEnd", "KolEnd"), 0),
    items: (Array.isArray(rawItems) ? rawItems : []).map(normalizeCardLine)
  };
};

const WarehouseProductCard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const idTov = toNumber(searchParams.get("idTov")) ?? undefined;

  const [idSkl, setIdSkl] = useState();
  const [dateBeg, setDateBeg] = useState(() => dayjs().startOf("month").format("YYYY-MM-DD"));
  const [dateEnd, setDateEnd] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [lookups, setLookups] = useState({ warehouses: [] });
  const [products, setProducts] = useState([]);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [scrollY, setScrollY] = useState(400);
  const bodyRef = useRef(null);
  const requestRef = useRef(0);
  const productTimerRef = useRef();
  const productRequestRef = useRef(0);

  useEffect(() => {
    fetchWarehouseLookups()
      .then((data) => setLookups(normalizeLookups(data)))
      .catch((e) => message.error(e.message || "Не удалось загрузить склады"));
  }, []);

  const loadProducts = (query = "") => {
    clearTimeout(productTimerRef.current);
    const requestId = ++productRequestRef.current;
    productTimerRef.current = setTimeout(() => {
      fetchSprTov(query)
        .then((data) => {
          if (requestId !== productRequestRef.current) return;
          const next = normalizeProductOptions(data);
          setProducts((previous) => {
            const selected = previous.find((item) => item.value === idTov);
            return selected && !next.some((item) => item.value === idTov) ? [selected, ...next] : next;
          });
        })
        .catch(() => undefined);
    }, 300);
  };

  useEffect(() => {
    if (idTov == null) return;
    if (products.some((item) => item.value === idTov)) return;
    let cancelled = false;
    fetchSprTovItem(idTov)
      .then((row) => {
        if (cancelled) return;
        const label = pick(row, "name", "Name") ?? "";
        setProducts((previous) => {
          if (previous.some((item) => item.value === idTov)) return previous;
          return [...previous, { value: idTov, label }];
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [idTov, products]);

  const load = useCallback(async () => {
    if (idTov == null) {
      setCard(null);
      setError("");
      return;
    }
    const requestId = ++requestRef.current;
    setLoading(true);
    setError("");
    try {
      const data = await fetchWarehouseProductCard({ idTov, idSkl, dateBeg, dateEnd });
      if (requestId !== requestRef.current) return;
      setCard(normalizeCard(data));
    } catch (e) {
      if (requestId !== requestRef.current) return;
      setCard(null);
      setError(e.message || "Не удалось загрузить данные");
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [idTov, idSkl, dateBeg, dateEnd]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const root = bodyRef.current;
    if (!root || typeof ResizeObserver === "undefined") return undefined;
    const update = () => setScrollY(Math.max(180, root.clientHeight - 40));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(root);
    return () => observer.disconnect();
  }, [card?.items?.length]);

  const onProductChange = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value == null) next.delete("idTov");
    else next.set("idTov", String(value));
    setSearchParams(next, { replace: true });
  };

  const columns = useMemo(
    () => [
      { title: "Дата", dataIndex: "fdate", key: "fdate", width: 110, render: formatDate },
      { title: "Склад", dataIndex: "sklName", key: "sklName", ellipsis: true },
      { title: "Вид", dataIndex: "vid", key: "vid", width: 190, render: (v) => formatMoveVid(v) },
      {
        title: "Источник",
        key: "source",
        width: 170,
        render: (_, record) =>
          record.vidOsn === 1 && record.idOsn ? (
            <Button
              type="link"
              size="small"
              className="warehouse-source-link"
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/warehouse/documents/${record.idOsn}`);
              }}
            >
              {`Накладная #${record.idOsn}`}
            </Button>
          ) : (
            `Источник ${record.vidOsn} #${record.idOsn ?? ""}`
          )
      },
      {
        title: "Приход",
        dataIndex: "kol",
        key: "kolPut",
        width: 110,
        align: "right",
        render: (v, record) => (record.vid > 0 ? formatNumber(v) : "")
      },
      {
        title: "Расход",
        dataIndex: "kol",
        key: "kolExp",
        width: 110,
        align: "right",
        render: (v, record) => (record.vid < 0 ? formatNumber(v) : "")
      },
      {
        title: "Остаток",
        dataIndex: "rest",
        key: "rest",
        width: 120,
        align: "right",
        render: formatNumber
      }
    ],
    [navigate]
  );

  const tiles = [
    { key: "beg", label: "На начало", value: card?.kolBeg },
    { key: "put", label: "Приход", value: card?.kolPut },
    { key: "exp", label: "Расход", value: card?.kolExp },
    { key: "end", label: "На конец", value: card?.kolEnd }
  ];

  return (
    <div className="warehouse-page">
      <div className="warehouse-toolbar">
        <Select
          allowClear
          showSearch
          filterOption={false}
          placeholder="Товар"
          options={products}
          value={idTov}
          onSearch={loadProducts}
          onDropdownVisibleChange={(visible) => visible && loadProducts("")}
          onChange={onProductChange}
          style={{ width: 280 }}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Склад"
          options={lookups.warehouses}
          value={idSkl}
          onChange={setIdSkl}
          style={{ width: 200 }}
        />
        <DatePicker
          value={dayjs(dateBeg)}
          allowClear={false}
          format="DD.MM.YYYY"
          onChange={(value) => setDateBeg(value.format("YYYY-MM-DD"))}
        />
        <DatePicker
          value={dayjs(dateEnd)}
          allowClear={false}
          format="DD.MM.YYYY"
          onChange={(value) => setDateEnd(value.format("YYYY-MM-DD"))}
        />
        <Tooltip title="Обновить">
          <Button icon={<ReloadOutlined />} loading={loading} onClick={load} />
        </Tooltip>
      </div>
      {error ? <Alert type="error" showIcon message={error} className="warehouse-error" /> : null}
      {idTov == null ? (
        <div className="warehouse-card-empty">Выберите товар</div>
      ) : (
        <>
          {card ? (
            <div className="warehouse-card-summary-wrap">
              <div className="warehouse-card-heading">
                {card.tovName}
                {card.funit ? <span className="warehouse-card-unit">, {card.funit}</span> : null}
              </div>
              <div className="warehouse-card-summary">
                {tiles.map((tile) => (
                  <div key={tile.key} className="warehouse-card-tile">
                    <div className="warehouse-card-tile-label">{tile.label}</div>
                    <div className="warehouse-card-tile-value">
                      {formatNumber(tile.value)}
                      {card.funit ? (
                        <span className="warehouse-card-tile-unit"> {card.funit}</span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <div className="warehouse-table-wrap" ref={bodyRef}>
            <Table
              size="small"
              rowKey="key"
              columns={columns}
              dataSource={card?.items || []}
              pagination={false}
              loading={loading}
              scroll={{ x: 900, y: scrollY }}
              locale={{ emptyText: "Движений за период нет" }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default WarehouseProductCard;
